import type { AccountUser, AppConfig } from '@doriavboard/shared';
import { create } from 'zustand';
import { ApiError } from '../api/client';
import { authApi } from '../api/endpoints';
import { clearBoardCaches } from '../lib/boardCache';

interface SessionState {
  status: 'loading' | 'ready';
  user: AccountUser | null;
  config: AppConfig | null;
  load(): Promise<void>;
  login(username: string, password: string): Promise<void>;
  register(input: { username: string; name: string; password: string }): Promise<void>;
  /** Goes on without an account, under a name. */
  continueAsGuest(name: string): Promise<void>;
  logout(): Promise<void>;
  setUser(user: AccountUser): void;
}

export const useSession = create<SessionState>((set) => ({
  status: 'loading',
  user: null,
  config: null,

  async load() {
    const [me, config] = await Promise.allSettled([authApi.me(), authApi.config()]);
    const user = me.status === 'fulfilled' ? me.value.user : null;
    if (me.status === 'rejected' && !(me.reason instanceof ApiError && me.reason.status === 401)) {
      console.warn('Could not load the session', me.reason);
    }
    set({
      status: 'ready',
      user,
      config:
        config.status === 'fulfilled'
          ? config.value
          : { allowRegistration: true, allowGuests: true, version: '' },
    });
  },

  async login(username, password) {
    const { user } = await authApi.login(username, password);
    set({ user });
  },

  async continueAsGuest(name) {
    const { user } = await authApi.guest(name);
    set({ user });
  },

  async register(input) {
    const { user } = await authApi.register(input);
    set({ user });
  },

  async logout() {
    await authApi.logout();
    set({ user: null });
    // Boards kept for offline use don't stay behind for whoever comes next.
    await clearBoardCaches();
  },

  setUser(user) {
    set({ user });
  },
}));
